import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { useLocation } from 'react-router-dom';
import { initQueryParams, replaceHistory, tableOptions, useQuery } from '../../../shared/lib/TableQueryParams';
import { PaginatedResponse } from '../../../shared/models';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG, API_RESPONSE, NO_PERMISSIONS_ERROR_MSG, ERROR_404, ERROR_409 } from '../../../shared/lib/Localization';
import {
    getInventoryReport,
    getInventoryReportSnapshots,
    getInventoryReportSnapshot,
    getInventoryReportSnapshotItems,
    createInventoryReportSnapshot,
    updateInventoryReportSnapshot,
    finalizeInventoryReportSnapshot,
    createStockOrderFromSnapshot,
    updateInventoryReportSnapshotItem,
    bulkUpdateInventoryReportSnapshotItems,
    prefillFromOtherSnapshot,
    getInventoryReportSnapshotTaskStatus,
    validateInventoryReportSnapshot,
    bulkResetInventoryReportSnapshotOrderQty
} from '../../api';
import { Typography, Button, Select, Menu, MenuItem, FormControl, InputLabel, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, LinearProgress, Collapse, IconButton, Popover, Tooltip, Chip, RadioGroup, Radio, FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow, InputAdornment } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, InfoOutlined as InfoOutlinedIcon, FilterList as FilterListIcon, Add as AddIcon, Input as InputIcon, CheckCircle as CheckCircleIcon, ShoppingCart as ShoppingCartIcon, Delete as DeleteIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, ArrowDropDown as ArrowDropDownIcon, Refresh as RefreshIcon, SaveAlt as SaveAltIcon, Save as SaveIcon, Search as SearchIcon, Close as CloseIcon } from '@material-ui/icons';
import {
    CustomTableTitle,
    ErrorStatusBar,
    ExportMenu,
    ExportStatusBar,
} from '../../../shared/components';
import download from 'downloadjs';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError, ToastWarning } from '../../../shared/components/Notifier';
import { Filter, FilterType } from '../../../shared/models/Filter';
import { AvailableFilters, SelectedFilters, FilterRow } from '../../../products/components/Filters';
import GlobalStyles from '../../../../styles/GlobalStyles.web';
import { blue, orange, red } from '@material-ui/core/colors';
import moment from 'moment';
import { InventoryReportSnapshotItemRow, UnsavedChange } from './types';
import InventoryOrderVirtualTable from './InventoryOrderVirtualTable';
import { mapSnapshotItemToRow, mapGetInventoryReportToRow, buildOrderByParam, buildValidationLookup } from './mapSnapshotItems';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        snapshotPanel: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: '#f5f5f5',
        },
        snapshotList: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            marginTop: theme.spacing(1),
        },
        actionButtons: {
            display: 'flex',
            gap: theme.spacing(1),
            marginTop: theme.spacing(2),
        },
        editableCell: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#e3f2fd',
            },
        },
        editingCell: {
            backgroundColor: '#fff3cd',
        },
        validationError: {
            color: red[600],
            fontSize: '0.75rem',
            marginTop: theme.spacing(0.5),
        },
        locationSelector: {
            minWidth: 200,
            flexShrink: 0,
        },
        snapshotControlsRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(1.5),
            '& > *': {
                flexShrink: 0,
            },
            '&:last-child': {
                marginBottom: 0,
            },
        },
        verticalHeader: {
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            whiteSpace: 'nowrap',
            height: '120px',
            padding: theme.spacing(1),
        },
        frozenHeader: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff',
        },
        frozenFirstColumn: {
            position: 'sticky',
            left: 0,
            zIndex: 9,
            backgroundColor: '#fff',
        },
        frozenSecondColumn: {
            position: 'sticky',
            left: 200,
            zIndex: 9,
            backgroundColor: '#fff',
        },
        denseCell: {
            padding: '2px 4px',
        },
        tableWrapper: {
            overflowX: 'auto',
            '& #inventory-order-report-table-scroll': {
                overflowX: 'auto',
            },
            '& .MuiTableContainer-root': {
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto',
            },
            '& table thead': {
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#fff',
            },
            '& table thead th': {
                position: 'sticky',
                top: 0,
                backgroundColor: '#fff !important',
            },
            '& table tbody td:nth-of-type(2), & table thead th:nth-of-type(2)': {
                position: 'sticky !important',
                left: '0 !important',
                zIndex: '9 !important',
                backgroundColor: '#fff !important',
                boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            },
            '& table tbody td:nth-of-type(3), & table thead th:nth-of-type(3)': {
                position: 'sticky !important',
                left: '320px !important',
                zIndex: '9 !important',
                backgroundColor: '#fff !important',
                boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            },
            '& table tbody td:nth-of-type(4), & table thead th:nth-of-type(4)': {
                position: 'sticky !important',
                left: '520px !important',
                zIndex: '9 !important',
                backgroundColor: '#fff !important',
                boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            },
        },
        errorPanel: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: '#fff8e1',
            borderLeft: `4px solid ${orange[600]}`,
        },
        errorListItem: {
            padding: theme.spacing(1),
            marginBottom: theme.spacing(1),
            backgroundColor: '#fff',
            borderRadius: 4,
            border: `1px solid ${orange[200]}`,
        },
        errorIcon: {
            color: orange[600],
            fontSize: '1.2rem',
            cursor: 'pointer',
            marginLeft: theme.spacing(0.5),
        },
        errorPopover: {
            padding: theme.spacing(1.5),
            maxWidth: 400,
        },
        processingOverlay: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(6, 2),
            backgroundColor: '#e8f4fd',
            borderRadius: 8,
            border: '1px solid #90caf9',
            marginBottom: theme.spacing(2),
            gap: theme.spacing(2),
        },
        processingProgressBar: {
            width: '100%',
            maxWidth: 560,
            borderRadius: 4,
        },
        tooltip: {
            fontSize: '1.05rem',
        },
    }),
);

import { ValidationError, InventoryReportSnapshot } from './types';

const FILTERS: Filter[] = [
    {
        id: 'f1',
        label: 'Supplier',
        types: [
            { id: '1', value: 'supplier', label: 'includes' },
            { id: '2', value: 'supplier__exclude', label: 'excludes' },
        ],
        selectedType: 'supplier',
        value: [],
        url: 'korona/supplier-list',
        labelField: 'name',
        bindValue: 'name',
        isResponsePaginated: true,
    },
    {
        id: 'f2',
        label: 'Short Items',
        types: [
            { id: '1', value: 'any_short', label: 'is equal' },
        ],
        selectedType: 'any_short',
        value: '',
        url: null,
        choices: [
            { id: '1', value: 'true', label: 'Yes' },
            { id: '2', value: 'false', label: 'No' },
        ],
    },
    {
        id: 'f3',
        label: 'Location',
        types: [
            { id: '1', value: 'location', label: 'includes' },
            { id: '2', value: 'location__exclude', label: 'excludes' },
        ],
        selectedType: 'location',
        value: [],
        url: 'core/locations',
        labelField: 'name',
        bindValue: 'id',
        isResponsePaginated: false,
    },
];

const InventoryOrderReportScreen = (props: any) => {
    const classes = useStyles({});
    const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
    const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
    const virtualizerRef = useRef<{ scrollToIndex: (index: number, options?: { align?: string }) => void } | null>(null);
    const [focusRowId, setFocusRowId] = useState<string | null>(null);
    let locationQuery = useQuery(useLocation);
    const defaultPageSize = 50;
    const initialQuery = () => {
        const q = initQueryParams(locationQuery);
        const pageSize = locationQuery.get('pageSize') ? Number(locationQuery.get('pageSize')) : defaultPageSize;
        return { ...q, pageSize };
    };
    const [tableQuery, setTableQuery] = useState<ReturnType<typeof initialQuery> & { _refresh?: number }>(initialQuery);
    const tableQueryRef = useRef(initialQuery());
    const fetchVersionRef = useRef(0);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchInputValue, setSearchInputValue] = useState(() => locationQuery.get('search') || '');

    useEffect(() => {
        tableQueryRef.current = tableQuery;
    }, [tableQuery]);

    useEffect(() => {
        setSearchInputValue(tableQuery.search ?? '');
    }, [tableQuery.search]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchInputValue(value);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setTableQuery((q: any) => ({ ...q, search: value, page: 0, _refresh: Date.now() }));
            searchDebounceRef.current = null;
        }, 400);
    }, []);
    const [exportBtnAnchorPos, setExportBtnAnchorPos] = React.useState<{ top: number; left: number } | null>(null);
    const [isExporting, setExporting] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [filterVisible, showFilters] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [filterString, setFilterString] = useState('');
    const [accountForReceived, setAccountForReceived] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [totalRecords, setTotalRecords] = useState(null as any);

    // Snapshot management state
    const [snapshots, setSnapshots] = useState<InventoryReportSnapshot[]>([]);
    const [selectedSnapshot, setSelectedSnapshot] = useState<InventoryReportSnapshot | null>(null);
    // Map row IDs to snapshot item IDs for quick lookup (use ref to avoid stale closures)
    const rowIdToSnapshotItemIdMap = useRef<Map<string, number>>(new Map());
    const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
    const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
    const [prefillDialogOpen, setPrefillDialogOpen] = useState(false);
    const [prefillConfirmDialogOpen, setPrefillConfirmDialogOpen] = useState(false);
    const [selectedPrefillSnapshotId, setSelectedPrefillSnapshotId] = useState<number | null>(null);
    // selectedLocationId removed in favor of custom filter
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const [reportData, setReportData] = useState<InventoryReportSnapshotItemRow[]>([]);
    const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
    const [stockOrderData, setStockOrderData] = useState<{
        supplier_id?: string;
        expected_from_date?: string;
        expected_to_date?: string;
        description?: string;
        comment?: string;
    }>({});
    const [progressMessage, setProgressMessage] = useState<string>('');
    const [taskStats, setTaskStats] = useState<{
        total: number;
        created: number;
        failed: number;
        remaining: number;
    } | null>(null);

    // Warning handling state
    const [validationErrorsList, setValidationErrorsList] = useState<ValidationError[]>([]);
    const [warningsTotalCount, setWarningsTotalCount] = useState(0);
    const [itemIdsWithWarnings, setItemIdsWithWarnings] = useState<number[]>([]);
    const [warningsPage, setWarningsPage] = useState(1);
    const [warningsPageSize] = useState(20);
    const [warningsTotalPages, setWarningsTotalPages] = useState(0);
    const [errorsExpanded, setErrorsExpanded] = useState(true);
    const [viewMode, setViewMode] = useState<'all' | 'warnings' | 'no_supplier'>('all');
    const [viewFilterAnchorPos, setViewFilterAnchorPos] = useState<{ top: number; left: number } | null>(null);
    const [errorPopoverAnchor, setErrorPopoverAnchor] = useState<{ [key: string]: Element | null }>({});
    const [isValidated, setIsValidated] = useState(false);
    const [selectedWarningItemId, setSelectedWarningItemId] = useState<number | null>(null);

    // Bulk edit state
    const [bulkEditAnchorPos, setBulkEditAnchorPos] = useState<{ top: number; left: number } | null>(null);
    const [bulkEditQty, setBulkEditQty] = useState<string>('0');
    const [bulkEditMode, setBulkEditMode] = useState<'ALL' | 'FILTERED' | 'VISIBLE' | 'RECALCULATE'>('ALL');
    const [isBulkEditing, setIsBulkEditing] = useState(false);

    // Bulk save mode: track changes locally, save via button
    const [bulkSaveMode, setBulkSaveMode] = useState(false);
    const bulkSaveModeRef = useRef(bulkSaveMode);
    useEffect(() => {
        bulkSaveModeRef.current = bulkSaveMode;
    }, [bulkSaveMode]);
    const [unsavedChanges, setUnsavedChanges] = useState<Record<string, UnsavedChange>>({});
    const unsavedChangesRef = useRef<Record<string, UnsavedChange>>({});
    const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const [bulkToSingleDiscardConfirmOpen, setBulkToSingleDiscardConfirmOpen] = useState(false);
    const [isSavingBulkChanges, setIsSavingBulkChanges] = useState(false);
    const [savingRowId, setSavingRowId] = useState<string | null>(null);

    // Get selected business and locations
    const selectedBusiness = props.userData?.selectedBusiness;
    const selectedLocations = props.userData?.selectedLocations || [];

    useEffect(() => {
        const businessId = selectedBusiness?.business_id ?? selectedBusiness?.id;
        if (businessId) {
            loadSnapshots();
        } else {
            setSnapshots([]);
        }
    }, [selectedBusiness?.business_id, selectedBusiness?.id]);

    // Clear unsaved changes when switching snapshots
    useEffect(() => {
        setUnsavedChanges({});
        unsavedChangesRef.current = {};
    }, [selectedSnapshot?.id]);

    // Keep ref in sync for data callback (avoids stale closure when table refetches right after edit)
    useEffect(() => {
        unsavedChangesRef.current = unsavedChanges;
    }, [unsavedChanges]);

    // Only changes where orig !== new (exclude no-ops for display and save)
    const actualUnsavedChangesEntries = React.useMemo(
        () => Object.entries(unsavedChanges).filter(([, c]) => c.originalValue !== c.newValue),
        [unsavedChanges]
    );

    // Reset to page 0 when short filter value changes so user sees first page of filtered results
    const shortFilterValue = selectedFilters.find((f: Filter) => f.id === 'f2')?.value;
    useEffect(() => {
        if (shortFilterValue === 'true' || shortFilterValue === 'false') {
            setTableQuery((q: any) => (q.page === 0 ? q : { ...q, page: 0, _refresh: Date.now() }));
        }
    }, [shortFilterValue]);

    // Fetch table data when snapshot, pagination, filters, or sort change
    useEffect(() => {
        if (!selectedSnapshot && !(props.userData?.selectedBusiness && selectedLocations.length > 0)) {
            setReportData([]);
            setTotalRecords(0);
            return;
        }
        setLoading(true);
        // Use tableQuery from state directly to avoid ref timing issues with page/pageSize
        // When filtering by single warning item, always use page 0 (single-item result has only 1 page)
        const page = selectedWarningItemId ? 0 : (tableQuery.page ?? 0);
        const pageSize = Math.max(1, Math.min(1000, Number(tableQuery.pageSize) || defaultPageSize));
        const search = tableQuery.search ?? '';
        const orderByField = tableQuery.orderBy;
        const orderDirection = tableQuery.orderDirection ?? 'asc';
        const orderBy = buildOrderByParam(orderByField, orderDirection);

        const version = ++fetchVersionRef.current;

        const doFetch = async () => {
            try {
                if (selectedSnapshot) {
                    const locationFilter = selectedFilters.find((f: Filter) => f.id === 'f3');
                    let locationId = null;
                    let locationIdExclude = null;
                    if (locationFilter?.value?.length) {
                        const ids = locationFilter.value.map((v: any) => String(v.id)).join(',');
                        if (locationFilter.selectedType === 'location__exclude') {
                            locationIdExclude = ids;
                        } else {
                            locationId = ids;
                        }
                    }
                    const supplierFilter = selectedFilters.find((f: Filter) => f.id === 'f1');
                    let supplier = null;
                    let supplierExclude = null;
                    if (supplierFilter?.value?.length) {
                        const names = supplierFilter.value.map((v: any) => v[supplierFilter.bindValue || 'name']).join(',');
                        if (supplierFilter.selectedType === 'supplier__exclude') {
                            supplierExclude = names;
                        } else {
                            supplier = names;
                        }
                    }
                    const shortFilter = selectedFilters.find((f: Filter) => f.id === 'f2');
                    // Pass 'true' or 'false' string when explicitly selected; null = no filter
                    const anyShort = (shortFilter?.value === 'true' || shortFilter?.value === 'false')
                        ? String(shortFilter.value)
                        : null;
                    const errorItemIds = viewMode === 'warnings'
                        ? (selectedWarningItemId ? [selectedWarningItemId] : itemIdsWithWarnings)
                        : null;
                    const noSupplier = viewMode === 'no_supplier';
                    const excludeNoSupplier = viewMode === 'all';

                    const data: PaginatedResponse<any> = await getInventoryReportSnapshotItems(
                        selectedSnapshot.id,
                        page + 1,
                        pageSize,
                        locationId,
                        supplier,
                        null, // supplier_icontains removed
                        supplierExclude,
                        locationIdExclude,
                        anyShort,
                        search,
                        errorItemIds,
                        orderBy,
                        noSupplier,
                        excludeNoSupplier,
                        accountForReceived
                    )
                        .catch(handleError)
                        .then(checkStatus)
                        .then(parseJSON);

                    if (version !== fetchVersionRef.current) return;

                    setErrorStatusVisible(false);
                    setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                    setTotalRecords(data.count);
                    const newMap = new Map<string, number>();
                    const validationLookup = buildValidationLookup(validationErrorsList, itemIdsWithWarnings);
                    const mappedData = (data.results || []).map((item: any) => {
                        const row = mapSnapshotItemToRow(item, {
                            validationErrorsList,
                            itemIdsWithWarnings,
                            validationLookup,
                            unsavedChanges: unsavedChangesRef.current,
                        });
                        if (row.snapshotItemId) {
                            newMap.set(row.id, row.snapshotItemId);
                        }
                        return row;
                    });
                    newMap.forEach((v, k) => rowIdToSnapshotItemIdMap.current.set(k, v));
                    setReportData(mappedData);
                } else {
                    const sb = props.userData.selectedBusiness;
                    const locationFilter = selectedFilters.find((f: Filter) => f.id === 'f3');
                    const locationIds = locationFilter?.value?.length
                        ? locationFilter.value.map((v: any) => v.id).join(',')
                        : selectedLocations.map((loc: any) => loc.id).join(',');
                    const orderByStr = orderBy || '';

                    const data: PaginatedResponse<any> = await getInventoryReport(
                        sb.business_id,
                        locationIds,
                        page + 1,
                        pageSize,
                        search,
                        orderByStr,
                        filterString,
                        false,
                        undefined,
                        accountForReceived
                    )
                        .catch(handleError)
                        .then(checkStatus)
                        .then(parseJSON);

                    if (version !== fetchVersionRef.current) return;

                    setErrorStatusVisible(false);
                    setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                    setTotalRecords(data.count);
                    const mappedData = (data.results || []).map(mapGetInventoryReportToRow);
                    setReportData(mappedData);
                }
                if (version !== fetchVersionRef.current) return;
                replaceHistory(
                    {
                        page,
                        pageSize,
                        search,
                        orderBy: orderByField ? { field: orderByField } : undefined,
                        orderDirection,
                        totalCount: 0,
                    },
                    props.history
                );
            } catch (error: any) {
                if (version !== fetchVersionRef.current) return;
                if (error?.status === 500) {
                    setEmptyDataSourceMessage(ERROR_500);
                } else if (error?.status === 403 || error?.status === 401) {
                    setEmptyDataSourceMessage(ERROR_MSG);
                    setErrorStatusVisible(true);
                } else {
                    setEmptyDataSourceMessage(ERROR_MSG);
                }
                setReportData([]);
                setTotalRecords(0);
            } finally {
                if (version === fetchVersionRef.current) {
                    setLoading(false);
                    setInitialLoad(false);
                }
            }
        };
        doFetch();
    }, [
        selectedSnapshot?.id,
        tableQuery.page,
        tableQuery.pageSize,
        tableQuery.search,
        tableQuery.orderBy,
        tableQuery.orderDirection,
        tableQuery._refresh,
        filterString,
        viewMode,
        selectedWarningItemId,
        selectedFilters,
        props.userData?.selectedBusiness,
        selectedLocations.length,
        validationErrorsList,
        itemIdsWithWarnings,
        accountForReceived,
    ]);


    useEffect(() => {
        const activeFilters = selectedFilters.filter(
            (f: Filter) => (f.selectedType && f.value && f.value.length > 0) || (f.selectedType.includes('range') && (f.minValue || f.maxValue))
        );
        let filterString = '';
        activeFilters.forEach((f: Filter) => {
            let filterValue = '';
            let isRangeFilter = f.selectedType.includes('range');
            if (f.url && Array.isArray(f.value)) {
                const mappedValues = f.value.map((v) => v[f.bindValue]);
                filterValue = mappedValues.toString();
            } else if (!isRangeFilter) {
                filterValue = f.value.toString();
            }
            if (isRangeFilter) {
                if (f.minValue) {
                    filterString += `&${f.selectedType}_min=${f.minValue}`;
                }
                if (f.maxValue) {
                    filterString += `&${f.selectedType}_max=${f.maxValue}`;
                }
            } else {
                filterString += `&${f.selectedType}=${filterValue}`;
            }
        });
        setFilterString(filterString);
    }, [selectedFilters]);

    const loadSnapshots = async () => {
        const businessId = selectedBusiness?.business_id ?? selectedBusiness?.id;
        if (!businessId) return;

        setIsLoadingSnapshots(true);
        try {
            const response = await getInventoryReportSnapshots(
                businessId
            )
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            // Map snapshots to ensure snapshotNumber field is available (camelCase)
            const snapshotsList = response.results || response;
            const mappedSnapshots = Array.isArray(snapshotsList)
                ? snapshotsList.map((snapshot: any) => ({
                    ...snapshot,
                    snapshotNumber: snapshot.snapshotNumber || snapshot.snapshot_number || `SNAP-${snapshot.id}`,
                    createdAt: snapshot.created_at || snapshot.createdAt,
                    finalizedAt: snapshot.finalized_at || snapshot.finalizedAt,
                }))
                : snapshotsList;

            setSnapshots(mappedSnapshots);
        } catch (error: any) {
            console.error('Error loading snapshots:', error);
            props.showToast({ message: 'Failed to load snapshots', options: ToastError });
        } finally {
            setIsLoadingSnapshots(false);
        }
    };

    const loadSnapshot = async (snapshotId: number) => {
        setLoading(true);
        try {
            // Fetch only snapshot metadata (no items)
            const response = await getInventoryReportSnapshot(snapshotId)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            // Map snapshot to ensure snapshotNumber field is available (camelCase)
            const mappedSnapshot = {
                ...response,
                snapshotNumber: response.snapshotNumber || response.snapshot_number || `SNAP-${response.id}`,
                createdAt: response.createdAt || response.created_at,
                finalizedAt: response.finalizedAt || response.finalized_at,
            };

            setSelectedSnapshot(mappedSnapshot);
            // Clear reportData since items will be loaded on-demand via pagination
            setReportData([]);
            // Reset validation state for new snapshot
            setIsValidated(false);
            setValidationErrorsList([]);
            setWarningsTotalCount(0);
            setItemIdsWithWarnings([]);
            setWarningsPage(1);
            setWarningsTotalPages(0);
            setViewMode('all');
            setViewFilterAnchorPos(null);
            // Clear the map when switching snapshots
            rowIdToSnapshotItemIdMap.current = new Map();
            // Table refresh is triggered by useEffect when selectedSnapshot/viewMode change
            // Do NOT setLoading(false) here - keep loader visible until useEffect's fetch completes.
            // Otherwise we briefly show "No records found" before items load.
            props.showToast({ message: 'Snapshot loaded successfully', options: ToastSuccess });
        } catch (error: any) {
            console.error('Error loading snapshot:', error);
            props.showToast({ message: 'Failed to load snapshot', options: ToastError });
            setLoading(false);
        }
    };

    const createSnapshot = async () => {
        if (!selectedBusiness) {
            props.showToast({ message: 'Please select a business', options: ToastWarning });
            return;
        }

        if (selectedLocations.length === 0) {
            props.showToast({ message: 'No locations available', options: ToastWarning });
            return;
        }

        setLoading(true);
        try {
            // Get all location IDs from selected locations
            const locationIds = selectedLocations.map((loc: any) => loc.id);

            // Create snapshot payload - backend will call stored procedure for data
            const snapshotPayload = {
                business_id: selectedBusiness.business_id,
                location_ids: locationIds,
            };

            const snapshotResponse = await createInventoryReportSnapshot(snapshotPayload)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            let createdSnapshots: any[] = [];
            if (Array.isArray(snapshotResponse)) {
                createdSnapshots = snapshotResponse;
            } else {
                createdSnapshots = [snapshotResponse];
            }

            if (createdSnapshots.length > 0) {
                const firstSnapshot = createdSnapshots[0];
                // Map snapshot to ensure snapshotNumber field is available (camelCase)
                const mappedSnapshot = {
                    ...firstSnapshot,
                    snapshotNumber: firstSnapshot.snapshotNumber || firstSnapshot.snapshot_number || `SNAP-${firstSnapshot.id}`,
                    createdAt: firstSnapshot.createdAt || firstSnapshot.created_at,
                    finalizedAt: firstSnapshot.finalizedAt || firstSnapshot.finalized_at,
                };

                props.showToast({ message: `${createdSnapshots.length} snapshot(s) created successfully`, options: ToastSuccess });
                await loadSnapshots();

                // Clear the map and report data to avoid stale references
                rowIdToSnapshotItemIdMap.current = new Map();
                setReportData([]);

                setSelectedSnapshot(mappedSnapshot);

                // Trigger table refresh to load snapshot items
                setTimeout(() => {
                    setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
                }, 100);
            }
            // Items will be loaded on-demand via pagination, no need to set reportData
        } catch (error: any) {
            console.error('Error creating snapshot:', error);
            props.showToast({ message: 'Failed to create snapshot', options: ToastError });
        } finally {
            setLoading(false);
        }
    };


    const finalizeSnapshot = async () => {
        if (!selectedSnapshot) return;

        setLoading(true);
        try {
            // Fetch all snapshot items to include in the request
            let allSnapshotItems: any[] = [];
            try {
                // Fetch all items by getting multiple pages (or use a large page size)
                let page = 1;
                let hasMore = true;

                while (hasMore) {
                    // Get location and supplier filters (match table fetch logic)
                    const locationFilter = selectedFilters.find((f: Filter) => f.id === 'f3');
                    let locationId: string | null = null;
                    let locationIdExclude: string | null = null;
                    if (locationFilter?.value?.length) {
                        const ids = locationFilter.value.map((v: any) => String(v.id)).join(',');
                        if (locationFilter.selectedType === 'location__exclude') {
                            locationIdExclude = ids;
                        } else {
                            locationId = ids;
                        }
                    }
                    const supplierFilter = selectedFilters.find((f: Filter) => f.id === 'f1');
                    let supplier: string | null = null;
                    let supplierExclude: string | null = null;
                    if (supplierFilter?.value?.length) {
                        const names = supplierFilter.value.map((v: any) => v[supplierFilter.bindValue || 'name']).join(',');
                        if (supplierFilter.selectedType === 'supplier__exclude') {
                            supplierExclude = names;
                        } else {
                            supplier = names;
                        }
                    }

                    const itemsResponse: any = await getInventoryReportSnapshotItems(
                        selectedSnapshot.id,
                        page,
                        1000, // Large page size to get all items at once
                        locationId,
                        supplier,
                        null, // supplier_icontains removed
                        supplierExclude,
                        locationIdExclude
                    )
                        .catch(handleError)
                        .then(checkStatus)
                        .then(parseJSON);

                    if (itemsResponse.results && itemsResponse.results.length > 0) {
                        allSnapshotItems = allSnapshotItems.concat(itemsResponse.results);
                        // Check if there are more pages
                        hasMore = itemsResponse.next !== null && itemsResponse.next !== undefined;
                        page++;
                    } else {
                        hasMore = false;
                    }
                }

            } catch (error: any) {
                // Continue anyway - backend will read from database
            }

            // Prepare the request data with create_stock_order flag and stock order details
            const requestData: any = {
                create_stock_order: true
            };

            // Include snapshot items with their order_qty_to_place values
            if (allSnapshotItems.length > 0) {
                requestData.snapshot_items = allSnapshotItems.map((item: any) => ({
                    id: item.id,
                    product_uuid: item.product_uuid,
                    order_qty_to_place: item.order_qty_to_place !== undefined && item.order_qty_to_place !== null
                        ? Number(item.order_qty_to_place)
                        : (item.quantity_to_order !== undefined && item.quantity_to_order !== null
                            ? Number(item.quantity_to_order)
                            : null),
                    quantity_to_order: item.quantity_to_order !== undefined && item.quantity_to_order !== null
                        ? Number(item.quantity_to_order)
                        : null
                }));
            }

            // Removed date fields and supplier_id as per requirements
            if (stockOrderData.description && stockOrderData.description.trim() !== '') {
                requestData.description = stockOrderData.description;
            }


            const response: any = await finalizeInventoryReportSnapshot(selectedSnapshot.id, requestData)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            if (response.status === 'processing') {
                // Check for validation warnings in the response (finalize returns full list, not paginated)
                if (response.validation_warnings && response.validation_warnings.length > 0) {
                    const warnings = response.validation_warnings;
                    setValidationErrorsList(warnings);
                    setWarningsTotalCount(warnings.length);
                    setItemIdsWithWarnings(warnings.map((w: any) => w.item_id));
                    setWarningsPage(1);
                    setWarningsTotalPages(1);
                    setErrorsExpanded(true);
                    props.showToast({ message: `Stock order creation started. ${warnings.length} item(s) have warnings.`, options: ToastWarning });
                } else {
                    props.showToast({ message: 'Stock order creation started in background...', options: ToastSuccess });
                }

                // Update local state to processing
                setSelectedSnapshot({
                    ...selectedSnapshot,
                    status: 'processing'
                });

                // Close dialog and reset form
                setFinalizeDialogOpen(false);
                setStockOrderData({});

                // Start polling
                pollSnapshotStatus(selectedSnapshot.id);
                setProgressMessage('Initializing stock order creation...');
            } else {
                props.showToast({ message: response.message || 'Snapshot finalized and stock order created successfully', options: ToastSuccess });
                await loadSnapshots();
                // Update selected snapshot with the finalized response
                if (response.snapshot) {
                    const mappedSnapshot = {
                        ...response.snapshot,
                        snapshotNumber: response.snapshot.snapshotNumber || response.snapshot.snapshot_number || `SNAP-${response.snapshot.id}`,
                        createdAt: response.snapshot.createdAt || response.snapshot.created_at,
                        finalizedAt: response.snapshot.finalizedAt || response.snapshot.finalized_at,
                    };
                    setSelectedSnapshot(mappedSnapshot);
                } else if (response.id) {
                    // If snapshot is directly in response (backward compatibility)
                    const mappedSnapshot = {
                        ...response,
                        snapshotNumber: response.snapshotNumber || response.snapshot_number || `SNAP-${response.id}`,
                        createdAt: response.createdAt || response.created_at,
                        finalizedAt: response.finalizedAt || response.finalized_at,
                    };
                    setSelectedSnapshot(mappedSnapshot);
                }
                // Close dialog and reset form
                setFinalizeDialogOpen(false);
                setStockOrderData({});
            }
        } catch (error: any) {
            console.error('Error finalizing snapshot and creating stock order:', error);

            // Check if this is a 400 validation error
            if (error && error.status === 400) {
                try {
                    // Parse the error response to extract validation errors
                    const errorData = error.data || error.response?.data;
                    if (errorData && errorData.validation_errors && Array.isArray(errorData.validation_errors)) {
                        const errs = errorData.validation_errors;
                        setValidationErrorsList(errs);
                        setWarningsTotalCount(errs.length);
                        setItemIdsWithWarnings(errs.map((e: any) => e.item_id));
                        setWarningsPage(1);
                        setWarningsTotalPages(1);
                        setErrorsExpanded(true);
                        props.showToast({
                            message: `Validation failed: ${errorData.validation_errors.length} item(s) have errors. Please check the error panel above.`,
                            options: ToastError
                        });
                    } else {
                        props.showToast({ message: 'Failed to finalize snapshot and create stock order', options: ToastError });
                    }
                } catch (parseError) {
                    console.error('Error parsing validation errors:', parseError);
                    props.showToast({ message: 'Failed to finalize snapshot and create stock order', options: ToastError });
                }
            } else {
                props.showToast({ message: 'Failed to finalize snapshot and create stock order', options: ToastError });
            }
        } finally {
            setLoading(false);
        }
    };

    // 2-step flow: Validate snapshot before creating stock order
    const validateSnapshot = async () => {
        if (!selectedSnapshot) return;

        setLoading(true);
        try {
            const response: any = await validateInventoryReportSnapshot(selectedSnapshot.id, 1, warningsPageSize)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            if (response.has_warnings && response.items_with_warnings > 0) {
                setValidationErrorsList(response.warnings || []);
                setWarningsTotalCount(response.items_with_warnings);
                setItemIdsWithWarnings(response.item_ids_with_warnings || []);
                setWarningsPage(response.page || 1);
                setWarningsTotalPages(response.total_pages || 1);
                setErrorsExpanded(true);
                props.showToast({
                    message: `Validation complete: ${response.items_with_warnings} item(s) have warnings. You can still proceed with stock order creation.`,
                    options: ToastWarning
                });
            } else {
                setValidationErrorsList([]);
                setWarningsTotalCount(0);
                setItemIdsWithWarnings([]);
                setItemIdsWithWarnings([]);
                setWarningsPage(1);
                setWarningsTotalPages(0);
                props.showToast({ message: 'Validation passed — no issues found.', options: ToastSuccess });
            }
            setIsValidated(true);
            setTableQuery((q: any) => ({ ...q, page: 0, _refresh: Date.now() }));
        } catch (error: any) {
            console.error('Error validating snapshot:', error);
            props.showToast({ message: 'Failed to validate snapshot', options: ToastError });
        } finally {
            setLoading(false);
        }
    };

    const fetchWarningsPage = async (page: number) => {
        if (!selectedSnapshot) return;
        try {
            const response: any = await validateInventoryReportSnapshot(selectedSnapshot.id, page, warningsPageSize)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);
            if (response.has_warnings) {
                setValidationErrorsList(response.warnings || []);
                setWarningsPage(response.page || page);
                setWarningsTotalPages(response.total_pages || 1);
                setItemIdsWithWarnings(response.item_ids_with_warnings || itemIdsWithWarnings);
            }
        } catch (error: any) {
            console.error('Error fetching warnings page:', error);
        }
    };

    const pollingIntervalRef = React.useRef<any>(null);

    const pollSnapshotStatus = (snapshotId: number) => {
        // Clear existing interval if any
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        let attempts = 0;

        pollingIntervalRef.current = setInterval(async () => {
            attempts++;
            try {
                // Poll task status endpoint
                const taskResponse = await getInventoryReportSnapshotTaskStatus(snapshotId)
                    .catch(() => null)
                    .then((res) => res ? checkStatus(res).then(parseJSON) : null);

                if (taskResponse) {
                    if (taskResponse.progress_message) {
                        setProgressMessage(taskResponse.progress_message);
                    }

                    // Update stats if the backend returned item counts
                    if (taskResponse.items_count !== undefined && taskResponse.items_count > 0) {
                        setTaskStats({
                            total: taskResponse.items_count,
                            created: taskResponse.items_success_count ?? 0,
                            failed: taskResponse.items_failure_count ?? 0,
                            remaining: taskResponse.items_pending_count ?? 0,
                        });
                    }

                    if (taskResponse.status === 'SUCCESS' || taskResponse.status === 'PARTIAL_SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        props.showToast({
                            message: taskResponse.status === 'SUCCESS' ? 'Stock order creation completed!' : 'Stock order creation completed with some errors.',
                            options: taskResponse.status === 'SUCCESS' ? ToastSuccess : ToastWarning
                        });
                        setProgressMessage('');
                        setTaskStats(null);

                        // Refresh snapshot
                        await loadSnapshot(snapshotId);
                    } else if (taskResponse.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        props.showToast({ message: `Stock order creation failed: ${taskResponse.progress_message}`, options: ToastError });
                        setProgressMessage('');
                        setTaskStats(null);
                        await loadSnapshot(snapshotId);
                    }
                    return;
                }

                // Fallback to snapshot status polling if task status fails or returns nothing (backward compatibility)
                const response = await getInventoryReportSnapshot(snapshotId)
                    .catch(() => null)
                    .then((res) => res ? checkStatus(res).then(parseJSON) : null);

                if (!response) return;

                if (response.status === 'finalized') {
                    clearInterval(pollingIntervalRef.current);
                    props.showToast({ message: 'Stock order creation completed!', options: ToastSuccess });
                    setProgressMessage('');
                    setTaskStats(null);
                    await loadSnapshot(snapshotId);
                } else if (response.status === 'failed') {
                    clearInterval(pollingIntervalRef.current);
                    props.showToast({ message: 'Stock order creation failed', options: ToastError });
                    setProgressMessage('');
                    setTaskStats(null);
                    await loadSnapshot(snapshotId);
                } else if (attempts >= 100) {
                    clearInterval(pollingIntervalRef.current);
                    props.showToast({ message: 'Stock order creation taking longer than expected. Please check back later.', options: ToastWarning });
                    setProgressMessage('');
                    setTaskStats(null);
                }
            } catch (error) {
                console.error("Polling exception:", error);
            }
        }, 2000);
    };

    // Cleanup polling on unmount
    React.useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);


    // Refresh table when warnings change to show inline warning icons
    React.useEffect(() => {
        if (itemIdsWithWarnings.length > 0) {
            setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        }
    }, [itemIdsWithWarnings]);

    // Refresh table when view filter changes (viewMode, selectedWarningItemId) or snapshot changes.
    // Using a single effect instead of calling onQueryChange in click handlers prevents
    // duplicate API calls. lastViewFilterRef deduplicates to avoid React Strict Mode double-invocation
    // and any re-render-induced duplicate refetches.
    const hasUserChangedWarningFilter = useRef(false);
    const lastViewFilterRef = useRef<string>('');
    React.useEffect(() => {
        if (!hasUserChangedWarningFilter.current) {
            hasUserChangedWarningFilter.current = true;
            return;
        }
        const filterKey = `${viewMode}-${selectedWarningItemId ?? ''}-${selectedSnapshot?.id ?? ''}`;
        if (filterKey === lastViewFilterRef.current) {
            return; // Skip duplicate call (e.g. React Strict Mode, or same filter re-applied)
        }
        lastViewFilterRef.current = filterKey;
        if (selectedSnapshot) {
            setTotalRecords(null);
            setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        }
    }, [viewMode, selectedWarningItemId, selectedSnapshot?.id]);


    const createStockOrder = async () => {
        if (!selectedSnapshot) return;

        setLoading(true);
        try {
            const response = await createStockOrderFromSnapshot(selectedSnapshot.id)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            props.showToast({ message: 'Stock order created successfully', options: ToastSuccess });
            // Optionally navigate to stock order detail
        } catch (error: any) {
            console.error('Error creating stock order:', error);
            props.showToast({ message: 'Failed to create stock order', options: ToastError });
        } finally {
            setLoading(false);
        }
    };

    const prefillFromOther = async (sourceSnapshotId: number) => {
        if (!selectedSnapshot) return;

        setLoading(true);
        try {
            // Use backend endpoint to prefill from other snapshot
            const response = await prefillFromOtherSnapshot(
                selectedSnapshot.id,
                sourceSnapshotId
            )
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            const updatedCount = response.updated_items || 0;
            const message = response.message || `Prefilled ${updatedCount} items from other snapshot`;

            props.showToast({ message, options: ToastSuccess });
            setPrefillDialogOpen(false);
            setPrefillConfirmDialogOpen(false);
            setSelectedPrefillSnapshotId(null);

            // Clear cached data and reload snapshot to get updated values
            setReportData([]);
            rowIdToSnapshotItemIdMap.current = new Map();
            setTotalRecords(null);

            // Reload the snapshot to refresh all data
            await loadSnapshot(selectedSnapshot.id);
        } catch (error: any) {
            console.error('Error prefilling from other snapshot:', error);
            const errorMessage = error?.response?.data?.error || error?.message || 'Failed to prefill from other snapshot';
            props.showToast({ message: errorMessage, options: ToastError });
        } finally {
            setLoading(false);
        }
    };

    const handlePrefillSnapshotSelect = (snapshotId: number) => {
        setSelectedPrefillSnapshotId(snapshotId);
        setPrefillConfirmDialogOpen(true);
    };

    const handleConfirmPrefill = () => {
        if (selectedPrefillSnapshotId && selectedSnapshot) {
            prefillFromOther(selectedPrefillSnapshotId);
        }
    };

    const validateQuantityToOrder = (row: InventoryReportSnapshotItemRow, newValue: number): string | null => {
        if (newValue < 0) {
            return 'Quantity must be positive';
        }

        const totalQuantity = row.onHandQuantity + newValue;
        const minQuantity = row.reorderLevel;
        const maxQuantity = row.maxOrderLevel;

        if (totalQuantity <= minQuantity) {
            return `Total quantity (${totalQuantity}) must be greater than reorder level (${minQuantity})`;
        }

        if (totalQuantity > maxQuantity) {
            return `Total quantity (${totalQuantity}) must be less than max order level (${maxQuantity})`;
        }

        return null;
    };


    const handleRefresh = () => {
        setTotalRecords(null);
        setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        if (selectedSnapshot) {
            loadSnapshot(selectedSnapshot.id);
        }
    };

    const toggleFilterPanel = () => {
        showFilters(!filterVisible);
    };

    const handleBulkReset = async () => {
        if (!selectedSnapshot) {
            props.showToast({ message: 'Please select a snapshot first', options: ToastWarning });
            return;
        }
        const isRecalculate = bulkEditMode === 'RECALCULATE';
        const qty = isRecalculate ? 0 : parseFloat(bulkEditQty);
        if (!isRecalculate && (isNaN(qty) || qty < 0)) {
            props.showToast({ message: 'Please enter a valid non-negative number', options: ToastWarning });
            return;
        }

        setIsBulkEditing(true);
        try {
            let options: any = {};

            if (bulkEditMode === 'VISIBLE') {
                // Collect IDs of currently visible rows from reportData
                const visibleIds = reportData
                    .map((r) => r.snapshotItemId)
                    .filter((id): id is number => id !== undefined && id !== null);
                if (visibleIds.length === 0) {
                    props.showToast({ message: 'No visible items found. Load the table first.', options: ToastWarning });
                    setIsBulkEditing(false);
                    return;
                }
                options.item_ids = visibleIds;
            } else if (bulkEditMode === 'FILTERED') {
                // Build filter params from active selectedFilters
                const filterParams: any = {};
                const activeFilters = (selectedFilters as Filter[]).filter(
                    (f) => f.value && (Array.isArray(f.value) ? f.value.length > 0 : f.value !== '')
                );
                activeFilters.forEach((f: Filter) => {
                    if (f.selectedType === 'location' && Array.isArray(f.value) && f.value.length > 0) {
                        filterParams.location_id = f.value.map((v: any) => String(v.id)).join(',');
                    } else if (f.selectedType === 'location__exclude' && Array.isArray(f.value) && f.value.length > 0) {
                        filterParams.location_id__exclude = f.value.map((v: any) => String(v.id)).join(',');
                    } else if (f.selectedType === 'supplier' && Array.isArray(f.value) && f.value.length > 0) {
                        filterParams.supplier = f.value.map((v: any) => v[f.bindValue || 'name']).join(',');
                    } else if (f.selectedType === 'supplier__exclude' && Array.isArray(f.value) && f.value.length > 0) {
                        filterParams.supplier__exclude = f.value.map((v: any) => v[f.bindValue || 'name']).join(',');
                    } else if (f.selectedType === 'any_short') {
                        filterParams.any_short = f.value;
                    }
                });
                // Also pass search term if any
                if (tableQuery && (tableQuery as any).search) {
                    filterParams.search = (tableQuery as any).search || (tableQueryRef.current as any).search;
                }
                options.filters = filterParams;
            }

            const response = await bulkResetInventoryReportSnapshotOrderQty(
                selectedSnapshot.id,
                bulkEditMode,
                qty,
                options
            )
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);

            props.showToast({
                message: isRecalculate
                    ? `Recalculated Order Qty for ${response.updated} item(s)`
                    : `Bulk update complete: ${response.updated} item(s) set to ${qty}`,
                options: ToastSuccess,
            });
            setBulkEditAnchorPos(null);
            // Reset validation state so warnings go away after recalculating to suggested values
            setValidationErrorsList([]);
            setItemIdsWithWarnings([]);
            setValidationErrors({});
            // Refresh table
            setTotalRecords(null);
            setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        } catch (error: any) {
            console.error('Error in bulk reset:', error);
            props.showToast({ message: 'Failed to apply bulk update', options: ToastError });
        } finally {
            setIsBulkEditing(false);
        }
    };

    const handleUnsavedChangesSave = async () => {
        if (!selectedSnapshot || actualUnsavedChangesEntries.length === 0) return;
        setIsSavingBulkChanges(true);
        try {
            const updates = actualUnsavedChangesEntries.map(([_, change]) => ({
                item_id: change.snapshotItemId,
                order_qty_to_place: change.newValue,
            }));
            await bulkUpdateInventoryReportSnapshotItems(selectedSnapshot.id, updates)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON);
            setUnsavedChanges({});
            unsavedChangesRef.current = {};
            setUnsavedChangesDialogOpen(false);
            setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
            props.showToast({ message: 'Changes saved successfully', options: ToastSuccess });
        } catch (error: any) {
            console.error('Error saving bulk changes:', error);
            props.showToast({ message: 'Failed to save changes', options: ToastError });
        } finally {
            setIsSavingBulkChanges(false);
        }
    };

    const handleUnsavedChangesReset = () => {
        setUnsavedChanges({});
        unsavedChangesRef.current = {};
        setUnsavedChangesDialogOpen(false);
        setResetConfirmOpen(false);
        setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        props.showToast({ message: 'Changes reverted', options: ToastSuccess });
    };

    const handleDiscardAndSwitchToSingleEdit = () => {
        setUnsavedChanges({});
        unsavedChangesRef.current = {};
        setBulkSaveMode(false);
        setBulkToSingleDiscardConfirmOpen(false);
        setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
        props.showToast({ message: 'Changes discarded. Switched to single edit mode.', options: ToastSuccess });
    };

    const handleBulkSaveModeChange = (checked: boolean) => {
        if (checked) {
            setBulkSaveMode(true);
        } else {
            if (actualUnsavedChangesEntries.length > 0) {
                setBulkToSingleDiscardConfirmOpen(true);
            } else {
                setBulkSaveMode(false);
            }
        }
    };

    const handleOrderQtyCommitAndNavigate = async (
        newValue: number,
        oldData: any,
        direction: 'next' | 'prev'
    ) => {
        const newData = { ...oldData, orderQtyToPlace: newValue };
        try {
            await handleRowUpdate(newData, oldData, { skipRefresh: bulkSaveModeRef.current });
        } catch (err) {
            return;
        }
        const editableRows = reportData.filter((r: any) => isEditable(r));
        const currentIndex = editableRows.findIndex((r: any) => r?.id === oldData?.id);
        if (currentIndex < 0) return;
        const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0 || nextIndex >= editableRows.length) return;
        const nextRow = editableRows[nextIndex];
        const nextRowIndexInReportData = reportData.findIndex((r) => r.id === nextRow.id);
        if (virtualizerRef.current && nextRowIndexInReportData >= 0) {
            virtualizerRef.current.scrollToIndex(nextRowIndexInReportData, { align: 'center' });
        }
        setFocusRowId(nextRow.id);
    };

    const isEditable = (rowData: any) =>
        !rowData?.noSupplier && !!selectedSnapshot && selectedSnapshot.status !== 'finalized';

    const addFilter = (filter: Filter) => {
        if (!selectedFilters.find((f: Filter) => f.id === filter.id)) {
            const filters = [...selectedFilters];
            filters.push(filter);
            setSelectedFilters(filters);
        }
    };

    const removeFilter = (index: number) => {
        const filters = [...selectedFilters];
        filters.splice(index, 1);
        setSelectedFilters(filters);
    };

    const updateFilter = (filter: Filter, index: number) => {
        const filters = [...selectedFilters];
        filters.splice(index, 1, filter);
        setSelectedFilters(filters);
    };

    const exportCSV = () => {
        const sb = props.userData?.selectedBusiness;
        const canExportFlat = sb && selectedLocations.length > 0;
        const canExportSnapshot = sb && selectedSnapshot != null;
        if (!canExportFlat && !canExportSnapshot) {
            return;
        }
        setExporting(true);

        // Use selected location filter or all locations
        const locationFilter = selectedFilters.find((f: Filter) => f.id === 'f3');
        let locationIds = '';
        if (locationFilter && locationFilter.value && locationFilter.value.length > 0) {
            locationIds = locationFilter.value.map((v: any) => v.id).join(',');
        } else {
            locationIds = selectedLocations.map((loc: any) => loc.id).join(',');
        }

        getInventoryReport(
            sb.business_id,
            locationIds,
            null,
            null,
            tableQuery.search || tableQueryRef.current.search,
            null,
            filterString,
            true,
            selectedSnapshot?.id ?? null,
            accountForReceived
        )
                .catch(handleError)
                .then(checkStatus)
                .then((res) => res.blob())
                .then((data) => {
                    download(data, 'Inventory_Order_Report.csv', 'text/csv');
                    setExporting(false);
                    setInitialLoad(false);
                })
                .catch((error: any) => {
                    setExporting(false);
                    if (error) {
                        if (error.status && error.status === 500) {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(false);
                        } else if (error.status === 403 || error.status === 401) {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(true);
                        } else {
                            setEmptyDataSourceMessage(ERROR_MSG);
                            setErrorStatusVisible(false);
                        }
                    }
                });
    };

    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const el: HTMLElement = ((event.currentTarget || event.target) as HTMLElement).closest('button') || (event.currentTarget || event.target as HTMLElement);
        const rect = el.getBoundingClientRect();
        setExportBtnAnchorPos({ top: rect.bottom, left: rect.right });
    };

    const handleExportMenuClose = (shouldExport: boolean) => {
        if (shouldExport) {
            exportCSV();
        }
        setExportBtnAnchorPos(null);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'created':
                return 'default';
            case 'editing':
                return 'primary';
            case 'finalized':
                return 'secondary';
            default:
                return 'default';
        }
    };

    const handleRowUpdate = (newData: any, oldData: any, options?: { skipRefresh?: boolean }) =>
        new Promise((resolve, reject) => {
            // If oldData is undefined (can happen in some MaterialTable contexts), use newData as base but try to find snapshotItemId
            const row = (oldData || newData) as InventoryReportSnapshotItemRow;

            // Reject edits for items without supplier info (read-only)
            if (row.noSupplier) {
                props.showToast({ message: 'Cannot edit items without supplier info', options: ToastWarning });
                reject(new Error('Cannot edit items without supplier'));
                return;
            }

            // Normalize values for comparison (handle string/number conversion)
            const oldOrderQty = parseFloat(String((oldData ? oldData.orderQtyToPlace : 0) || 0));
            const newOrderQty = parseFloat(String(newData.orderQtyToPlace || 0));
            const oldQuantityToOrder = parseFloat(String((oldData ? oldData.quantityToOrder : 0) || 0));
            const newQuantityToOrder = parseFloat(String(newData.quantityToOrder || 0));

            // Check if orderQtyToPlace field was edited (use more lenient comparison)
            const orderQtyToPlaceChanged = Math.abs(oldOrderQty - newOrderQty) > 0.0001;
            const quantityToOrderChanged = Math.abs(oldQuantityToOrder - newQuantityToOrder) > 0.0001;

            // Validate orderQtyToPlace field
            if (newOrderQty < 0) {
                const errorKey = `${row.id}_orderQtyToPlace`;
                setValidationErrors({
                    ...validationErrors,
                    [errorKey]: 'Order quantity must be positive',
                });
                reject(new Error('Order quantity must be positive'));
                return;
            }

            // Validate quantity to order if changed
            if (quantityToOrderChanged) {
                const error = validateQuantityToOrder(row, newQuantityToOrder);

                if (error) {
                    setValidationErrors({
                        ...validationErrors,
                        [`${row.id}_quantityToOrder`]: error,
                    });
                    reject(new Error(error));
                    return;
                }
            }

            // Clear validation errors
            const newErrors = { ...validationErrors };
            delete newErrors[`${row.id}_orderQtyToPlace`];
            delete newErrors[`${row.id}_quantityToOrder`];
            setValidationErrors(newErrors);

            // If we're editing a snapshot, save to backend
            // Always save orderQtyToPlace if we're in a snapshot, even if value appears unchanged
            // (Material-Table might not detect changes properly)
            if (selectedSnapshot && selectedSnapshot.status !== 'finalized') {
                // Try to get snapshotItemId from multiple sources
                // Material-Table may drop it from newData, so check oldData first
                let itemId = (oldData && oldData.snapshotItemId) || row.snapshotItemId || newData.snapshotItemId;

                // If still not found, look it up from our map using row.id
                if (!itemId && row.id) {
                    itemId = rowIdToSnapshotItemIdMap.current.get(row.id);
                }

                // If map is empty or itemId still not found, try to find it in reportData
                if (!itemId && row.id && reportData.length > 0) {
                    const foundRow = reportData.find(r => r.id === row.id);
                    if (foundRow && foundRow.snapshotItemId) {
                        itemId = foundRow.snapshotItemId;
                        // Also add it to the map for future lookups
                        rowIdToSnapshotItemIdMap.current.set(row.id, itemId);
                    }
                }

                // Last resort: if map is empty, try to rebuild it from reportData
                if (!itemId && row.id && rowIdToSnapshotItemIdMap.current.size === 0 && reportData.length > 0) {
                    reportData.forEach(r => {
                        if (r.snapshotItemId && r.id) {
                            rowIdToSnapshotItemIdMap.current.set(r.id, r.snapshotItemId);
                        }
                    });
                    itemId = rowIdToSnapshotItemIdMap.current.get(row.id);
                }

                if (!itemId) {
                    // If map is empty, the table data might not have loaded yet
                    if (rowIdToSnapshotItemIdMap.current.size === 0) {
                        props.showToast({
                            message: 'Data is still loading. Please wait a moment and try again, or refresh the page.',
                            options: ToastWarning
                        });
                    } else {
                        props.showToast({
                            message: 'Cannot save: Item ID not found for this row. Please refresh the page and try again.',
                            options: ToastError
                        });
                    }
                    reject(new Error('Item ID not found'));
                    return;
                }

                const updateData: any = {};

                // Always include orderQtyToPlace if it's being edited (field is editable)
                // Material-Table calls onRowUpdate when any editable field is changed
                // Ensure value is never negative
                updateData.order_qty_to_place = Math.max(0, newOrderQty);

                if (quantityToOrderChanged) {
                    updateData.quantity_to_order = newQuantityToOrder;
                }

                // Bulk save mode: track locally, don't call API (use ref to avoid stale closure when toggling bulk save and editing quickly)
                if (bulkSaveModeRef.current) {
                    const originalValue = unsavedChanges[row.id]?.originalValue ?? (oldData?.originalOrderQtyToPlace ?? oldOrderQty);
                    const change: UnsavedChange = {
                        newValue: Math.max(0, newOrderQty),
                        originalValue,
                        description: row.description || '',
                        supplier: row.supplier || '',
                        location: row.location || '',
                        snapshotItemId: Number(itemId),
                    };
                    setUnsavedChanges(prev => ({ ...prev, [row.id]: change }));
                    unsavedChangesRef.current = { ...unsavedChangesRef.current, [row.id]: change };
                    resolve(newData);
                    return;
                }

                setSavingRowId(row.id);
                updateInventoryReportSnapshotItem(
                    selectedSnapshot.id,
                    itemId,
                    updateData
                )
                    .catch(handleError)
                    .then(checkStatus)
                    .then(parseJSON)
                    .then(() => {
                        setSavingRowId(null);
                        props.showToast({ message: 'Row updated successfully', options: ToastSuccess });
                        if (!bulkSaveModeRef.current && oldData?.id) {
                            const newValue = Math.max(0, newOrderQty);
                            setReportData((prev) => {
                                const idx = prev.findIndex((r) => r.id === oldData.id);
                                if (idx < 0) return prev;
                                const updated = [...prev];
                                updated[idx] = {
                                    ...updated[idx],
                                    orderQtyToPlace: newValue,
                                    originalOrderQtyToPlace: newValue,
                                };
                                return updated;
                            });
                        }
                        if (options?.skipRefresh !== true) {
                            setTableQuery((q: any) => ({ ...q, _refresh: Date.now() }));
                        }
                        resolve(newData);
                    })
                    .catch((error: any) => {
                        setSavingRowId(null);
                        console.error('Error saving row:', error);

                        // Check if error has validation_errors from backend
                        if (error.validation_errors && Array.isArray(error.validation_errors)) {
                            const errorMessage = error.validation_errors.join(', ');
                            props.showToast({
                                message: `Validation failed: ${errorMessage}`,
                                options: ToastError
                            });
                        } else if (error.error) {
                            props.showToast({
                                message: error.error,
                                options: ToastError
                            });
                        } else {
                            props.showToast({
                                message: 'Failed to save row',
                                options: ToastError
                            });
                        }
                        reject(error);
                    });
            } else {
                // Show error message when trying to edit without a snapshot
                props.showToast({
                    message: 'Please select or create a snapshot first to save order values. Changes will not be saved without a snapshot.',
                    options: ToastWarning
                });
                reject(new Error('No snapshot selected. Please select or create a snapshot to save changes.'));
                return;
            }
        });

    return (
        <Container maxWidth={false}>
            <ErrorStatusBar isVisible={isErrorStatusVisible} />
            <ExportStatusBar isVisible={isExporting} />

            {/* Snapshot Management Panel - REMOVED and moved to Toolbar */}

            {/* Validation Warnings Panel */}
            {warningsTotalCount > 0 && (
                <Paper className={classes.errorPanel}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InfoOutlinedIcon style={{ color: orange[600], marginRight: 8 }} />
                            <Typography variant="h6" style={{ color: orange[800] }}>
                                Validation Warnings
                            </Typography>
                            <Chip
                                label={warningsTotalCount}
                                size="small"
                                style={{ marginLeft: 8, backgroundColor: orange[600], color: '#fff' }}
                            />
                        </div>
                        <IconButton
                            size="small"
                            onClick={() => setErrorsExpanded(!errorsExpanded)}
                            aria-label={errorsExpanded ? "Collapse warnings" : "Expand warnings"}
                        >
                            {errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </div>
                    <Collapse in={errorsExpanded}>
                        <Typography variant="body2" style={{ marginBottom: 12, color: orange[800] }}>
                            These are suggestions. You can override and proceed with stock order creation.
                        </Typography>
                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                            {validationErrorsList.map((error, index) => (
                                <div
                                    key={index}
                                    className={classes.errorListItem}
                                    onClick={() => {
                                        // Filter table to show only this item (useEffect triggers table refresh)
                                        setSelectedWarningItemId(error.item_id);
                                        setViewMode('warnings');
                                        setTotalRecords(null);
                                        setTableQuery((q: any) => ({ ...q, page: 0 }));
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        backgroundColor: selectedWarningItemId === error.item_id ? orange[100] : 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedWarningItemId !== error.item_id) {
                                            e.currentTarget.style.backgroundColor = orange[50];
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedWarningItemId !== error.item_id) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        } else {
                                            e.currentTarget.style.backgroundColor = orange[100];
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                        {error.korona_product_number ? (
                                            <>
                                                Product #{error.korona_product_number}
                                                {error.location_name && ` • ${error.location_name}`}
                                                {error.supplier_name && ` • ${error.supplier_name}`}
                                                {error.description && ` - ${error.description}`}
                                            </>
                                        ) : (
                                            `Item ID: ${error.item_id} - ${error.description}`
                                        )}
                                    </Typography>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        {(error.warnings || error.errors || []).map((warn, warnIdx) => (
                                            <li key={warnIdx}>
                                                <Typography variant="body2" style={{ color: orange[700] }}>
                                                    {warn}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        {warningsTotalPages > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => fetchWarningsPage(warningsPage - 1)}
                                    disabled={warningsPage <= 1}
                                    aria-label="Previous page"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body2" style={{ color: orange[800] }}>
                                    Page {warningsPage} of {warningsTotalPages}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => fetchWarningsPage(warningsPage + 1)}
                                    disabled={warningsPage >= warningsTotalPages}
                                    aria-label="Next page"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </div>
                        )}
                    </Collapse>
                </Paper>
            )}

            {/* Processing Overlay - shown while stock orders are being created */}
            {selectedSnapshot && selectedSnapshot.status === 'processing' && (
                <div className={classes.processingOverlay}>
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="h6" style={{ fontWeight: 600, color: '#1565c0' }}>
                        Creating Stock Orders…
                    </Typography>
                    {progressMessage && (
                        <Typography variant="body1" style={{ color: '#1976d2', textAlign: 'center' }}>
                            {progressMessage}
                        </Typography>
                    )}
                    {taskStats && taskStats.total > 0 && (
                        <>
                            {/* Stat chips row */}
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Chip
                                    label={`${taskStats.total} total`}
                                    size="small"
                                    style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                                />
                                <Chip
                                    label={`${taskStats.created} created`}
                                    size="small"
                                    style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                />
                                {taskStats.failed > 0 && (
                                    <Chip
                                        label={`${taskStats.failed} failed`}
                                        size="small"
                                        style={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }}
                                    />
                                )}
                                <Chip
                                    label={`${taskStats.remaining} remaining`}
                                    size="small"
                                    style={{ backgroundColor: '#fff8e1', color: '#e65100', fontWeight: 600 }}
                                />
                            </div>
                            {/* Deterministic progress bar */}
                            <LinearProgress
                                className={classes.processingProgressBar}
                                variant="determinate"
                                value={taskStats.total > 0 ? Math.round(((taskStats.created + taskStats.failed) / taskStats.total) * 100) : 0}
                                color="primary"
                            />
                        </>
                    )}
                    {(!taskStats || taskStats.total === 0) && (
                        <LinearProgress
                            className={classes.processingProgressBar}
                            color="primary"
                        />
                    )}
                    <Typography variant="caption" color="textSecondary">
                        Please wait. The table will refresh automatically when complete.
                    </Typography>
                </div>
            )}

            {/* Main Report Table */}
            <div
                className={`table-wrapper ${classes.tableWrapper}`}
                id="inventory-order-report-table"
                style={selectedSnapshot?.status === 'processing' ? { pointerEvents: 'none', opacity: 0.35 } : undefined}
            >
                <style>{`
                    #inventory-order-report-table table {
                        border-collapse: separate !important;
                        border-spacing: 0 !important;
                    }
                    /* Firefox-specific fixes - ensure table structure is maintained */
                    @-moz-document url-prefix() {
                        #inventory-order-report-table .MuiTableContainer-root {
                            overflow: auto !important;
                            -moz-overflow-scrolling: touch;
                        }
                        /* Critical: Firefox needs explicit table display types to maintain structure */
                        #inventory-order-report-table table {
                            width: 100% !important;
                            table-layout: auto !important;
                            border-collapse: separate !important;
                            border-spacing: 0 !important;
                            display: table !important;
                            border: 0 !important;
                        }
                        /* Fix extremely large calculated widths in Firefox - override Material-UI's width calculation */
                        #inventory-order-report-table table th,
                        #inventory-order-report-table table td {
                            width: auto !important;
                            max-width: 300px !important;
                            min-width: 0 !important;
                        }
                        /* Prevent calc() from creating huge widths */
                        #inventory-order-report-table table th[style*="calc"],
                        #inventory-order-report-table table td[style*="calc"] {
                            width: auto !important;
                            max-width: 300px !important;
                        }
                        /* Set specific width for Actions column (1st) - must be fixed to accommodate edit/save/cancel icons */
                        #inventory-order-report-table table th:nth-child(1),
                        #inventory-order-report-table table td:nth-child(1) {
                            width: 90px !important;
                            max-width: 90px !important;
                            min-width: 90px !important;
                            padding: 4px !important;
                        }
                        /* Set specific widths for Description column (2nd) */
                        #inventory-order-report-table table th:nth-child(2),
                        #inventory-order-report-table table td:nth-child(2) {
                            width: 320px !important;
                            max-width: 320px !important;
                            min-width: 320px !important;
                        }
                        /* Set specific widths for Supplier column (3rd) */
                        #inventory-order-report-table table th:nth-child(3),
                        #inventory-order-report-table table td:nth-child(3) {
                            width: 200px !important;
                            max-width: 200px !important;
                            min-width: 200px !important;
                        }
                        /* Set specific widths for Location column (4th) */
                        #inventory-order-report-table table th:nth-child(4),
                        #inventory-order-report-table table td:nth-child(4) {
                            width: 150px !important;
                            max-width: 150px !important;
                            min-width: 150px !important;
                        }
                        /* Set specific widths for On-Hand Qty column (5th) */
                        #inventory-order-report-table table th:nth-child(5),
                        #inventory-order-report-table table td:nth-child(5) {
                            width: 100px !important;
                            max-width: 100px !important;
                            min-width: 100px !important;
                        }
                        /* Set narrower widths for numeric columns (starting from column 6) */
                        #inventory-order-report-table table th:nth-child(n+6),
                        #inventory-order-report-table table td:nth-child(n+6) {
                            width: auto !important;
                            max-width: 100px !important;
                            min-width: 50px !important;
                        }
                        #inventory-order-report-table table colgroup {
                            display: table-column-group !important;
                        }
                        #inventory-order-report-table table thead {
                            display: table-header-group !important;
                        }
                        #inventory-order-report-table table tbody {
                            display: table-row-group !important;
                        }
                        #inventory-order-report-table table tfoot {
                            display: table-footer-group !important;
                        }
                        #inventory-order-report-table table tr {
                            display: table-row !important;
                        }
                        #inventory-order-report-table table th,
                        #inventory-order-report-table table td {
                            display: table-cell !important;
                        }
                    }
                    #inventory-order-report-table table thead {
                        position: sticky !important;
                        top: 0 !important;
                        z-index: 10 !important;
                        background-color: #fff !important;
                    }
                    #inventory-order-report-table table thead {
                        vertical-align: bottom !important;
                    }
                    #inventory-order-report-table table thead th {
                        position: sticky !important;
                        top: 0 !important;
                        background-color: #fff !important;
                        vertical-align: bottom !important;
                        display: table-cell !important;
                        padding-bottom: 8px !important;
                    }
                    /* For headers with vertical text, ensure content aligns to bottom */
                    #inventory-order-report-table table thead th .MuiTableSortLabel-root,
                    #inventory-order-report-table table thead th button {
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        justify-content: flex-end !important;
                        height: 100% !important;
                        width: 100% !important;
                        min-height: 100% !important;
                        padding: 0 !important;
                    }
                    /* Ensure the sort label text aligns to bottom */
                    #inventory-order-report-table table thead th .MuiTableSortLabel-root > span {
                        display: flex !important;
                        flex-direction: column !important;
                        justify-content: flex-end !important;
                        align-items: center !important;
                        height: 100% !important;
                    }
                    /* Actions column (1st) - frozen on the left */
                    #inventory-order-report-table table tbody tr td:nth-child(1) {
                        position: sticky !important;
                        left: 0 !important;
                        z-index: 9 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    #inventory-order-report-table table thead tr th:nth-child(1) {
                        position: sticky !important;
                        left: 0 !important;
                        top: 0 !important;
                        z-index: 11 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    /* Description column (2nd) - frozen */
                    #inventory-order-report-table table tbody tr td:nth-child(2) {
                        position: sticky !important;
                        left: 90px !important;
                        z-index: 9 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    #inventory-order-report-table table thead tr th:nth-child(2) {
                        position: sticky !important;
                        left: 90px !important;
                        top: 0 !important;
                        z-index: 11 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    /* Supplier column (3rd) - frozen */
                    #inventory-order-report-table table tbody tr td:nth-child(3) {
                        position: sticky !important;
                        left: 290px !important;
                        z-index: 9 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    #inventory-order-report-table table thead tr th:nth-child(3) {
                        position: sticky !important;
                        left: 290px !important;
                        top: 0 !important;
                        z-index: 11 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    /* Location column (4th) - frozen */
                    #inventory-order-report-table table tbody tr td:nth-child(4) {
                        position: sticky !important;
                        left: 490px !important;
                        z-index: 9 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    #inventory-order-report-table table thead tr th:nth-child(4) {
                        position: sticky !important;
                        left: 490px !important;
                        top: 0 !important;
                        z-index: 11 !important;
                        background-color: #fff !important;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important;
                    }
                    /* Firefox-specific fixes for sticky columns - must come after general sticky rules */
                    @-moz-document url-prefix() {
                        /* Ensure table structure is maintained even with sticky positioning */
                        #inventory-order-report-table table tbody tr {
                            display: table-row !important;
                        }
                        #inventory-order-report-table table tbody tr td {
                            display: table-cell !important;
                        }
                        #inventory-order-report-table table thead tr {
                            display: table-row !important;
                        }
                        #inventory-order-report-table table thead tr th {
                            display: table-cell !important;
                        }
                        /* Apply sticky positioning after ensuring display types - all frozen columns including Actions */
                        #inventory-order-report-table table tbody tr td:nth-child(1),
                        #inventory-order-report-table table thead tr th:nth-child(1),
                        #inventory-order-report-table table tbody tr td:nth-child(2),
                        #inventory-order-report-table table thead tr th:nth-child(2),
                        #inventory-order-report-table table tbody tr td:nth-child(3),
                        #inventory-order-report-table table thead tr th:nth-child(3),
                        #inventory-order-report-table table tbody tr td:nth-child(4),
                        #inventory-order-report-table table thead tr th:nth-child(4) {
                            position: -moz-sticky !important;
                            position: sticky !important;
                        }
                    }
                    /* Ensure On-Hand Qty (column 5) is NOT frozen */
                    #inventory-order-report-table table tbody tr td:nth-child(5),
                    #inventory-order-report-table table thead tr th:nth-child(5) {
                        position: static !important;
                        left: auto !important;
                        z-index: auto !important;
                    }
                    /* Firefox-specific: Fix Actions column width */
                    @-moz-document url-prefix() {
                        #inventory-order-report-table table tbody tr td:nth-child(1),
                        #inventory-order-report-table table thead tr th:nth-child(1) {
                            width: 90px !important;
                            max-width: 90px !important;
                            min-width: 90px !important;
                            padding: 4px !important;
                        }
                    }
                    /* Improve MaterialTable toolbar alignment and spacing */
                    #inventory-order-report-table .MuiToolbar-root {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: space-between !important;
                        padding: 8px 16px !important;
                        min-height: 64px !important;
                        flex-wrap: nowrap !important;
                        gap: 16px !important;
                    }
                    /* Title section - left aligned with proper spacing */
                    #inventory-order-report-table .MuiToolbar-root > div:first-child {
                        display: flex !important;
                        align-items: center !important;
                        flex: 0 0 auto !important;
                        min-width: 0 !important;
                        margin-right: 16px !important;
                    }
                    /* Search section - centered with proper spacing */
                    #inventory-order-report-table .MuiToolbar-root > div:nth-child(2) {
                        display: flex !important;
                        align-items: center !important;
                        flex: 1 1 auto !important;
                        justify-content: center !important;
                        min-width: 200px !important;
                        max-width: 400px !important;
                        margin: 0 16px !important;
                    }
                    /* Action buttons section - right aligned with proper spacing */
                    #inventory-order-report-table .MuiToolbar-root > div:last-child {
                        display: flex !important;
                        align-items: center !important;
                        flex: 0 0 auto !important;
                        gap: 8px !important;
                        margin-left: 16px !important;
                    }
                    /* Space out action buttons */
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Refresh"],
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Export"],
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Filter"],
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Refresh Data"],
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Show Filters"],
                    #inventory-order-report-table .MuiToolbar-root button[aria-label*="Hide Filters"] {
                        margin: 0 !important;
                        padding: 8px !important;
                    }
                    /* Search input styling */
                    #inventory-order-report-table .MuiToolbar-root input[type="text"] {
                        min-width: 200px !important;
                        max-width: 350px !important;
                        width: 100% !important;
                    }
                    /* Better alignment for search container */
                    #inventory-order-report-table .MuiToolbar-root form {
                        display: flex !important;
                        align-items: center !important;
                        width: 100% !important;
                        max-width: 350px !important;
                    }
                    /* Better spacing on smaller screens */
                    @media (max-width: 960px) {
                        #inventory-order-report-table .MuiToolbar-root {
                            flex-wrap: wrap !important;
                            gap: 12px !important;
                        }
                        #inventory-order-report-table .MuiToolbar-root > div:first-child {
                            margin-right: 0 !important;
                            flex-basis: 100% !important;
                        }
                        #inventory-order-report-table .MuiToolbar-root > div:nth-child(2) {
                            margin: 0 !important;
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                        }
                        #inventory-order-report-table .MuiToolbar-root > div:last-child {
                            margin-left: 0 !important;
                            justify-content: flex-start !important;
                            flex-basis: 100% !important;
                        }
                    }
                    /* Robust fix for pagination layout */
                    #inventory-order-report-table {
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow-x: auto !important;
                        overflow-y: visible !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                    #inventory-order-report-table .MuiPaper-root {
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow: hidden !important;
                        display: flex !important;
                        flex-direction: column !important;
                        box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12) !important;
                    }
                    #inventory-order-report-table .MuiTableContainer-root {
                        overflow-x: auto !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        flex: 1 1 auto !important;
                    }
                `}</style>
                <div style={{ backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                    {/* Single line: Title + Snapshot controls (left) | Search + Table actions (right) */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px 8px 8px', flexWrap: 'nowrap', gap: 12 }}>
                        {/* Left: Title + Snapshot dropdown + snapshot action buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 2, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                            <CustomTableTitle history={props.history} title={'Inventory Replenishment Hub'} icon={SVG_ICONS.product} compactBackButton />
                            <Autocomplete<InventoryReportSnapshot>
                                id="snapshot-select-toolbar"
                                options={snapshots}
                                value={selectedSnapshot}
                                getOptionLabel={(option) => `#${option.snapshotNumber} (ID: ${option.id}) - ${moment(option.createdAt).format('MM/DD/YYYY')} (${option.status})`}
                                getOptionDisabled={(option) => option.status === 'finalized'}
                                getOptionSelected={(option, value) => option.id === (value as InventoryReportSnapshot)?.id}
                                onChange={(_, newValue) => {
                                    const snapshot = newValue as InventoryReportSnapshot | null;
                                    if (!snapshot) {
                                        setSelectedSnapshot(null);
                                        rowIdToSnapshotItemIdMap.current = new Map();
                                        setReportData([]);
                                        setIsValidated(false);
                                        setValidationErrorsList([]);
                                        setWarningsTotalCount(0);
                                        setItemIdsWithWarnings([]);
                                        setViewMode('all');
                                        setViewFilterAnchorPos(null);
                                    } else if (snapshot.status !== 'finalized') {
                                        loadSnapshot(snapshot.id);
                                    } else {
                                        const mappedSnapshot = {
                                            ...(snapshot as any),
                                            snapshotNumber: (snapshot as any).snapshot_number || `SNAP-${snapshot.id}`,
                                            createdAt: (snapshot as any).created_at,
                                            finalizedAt: (snapshot as any).finalized_at,
                                        };
                                        setSelectedSnapshot(mappedSnapshot);
                                        setIsValidated(false);
                                        setValidationErrorsList([]);
                                        setWarningsTotalCount(0);
                                        setItemIdsWithWarnings([]);
                                        setViewMode('all');
                                        setViewFilterAnchorPos(null);
                                        rowIdToSnapshotItemIdMap.current = new Map();
                                        setReportData([]);
                                        props.showToast({ message: 'This snapshot is finalized and cannot be edited', options: ToastWarning });
                                    }
                                }}
                                style={{ minWidth: 320, maxWidth: 420, marginRight: 4, flex: '1 1 auto' }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Snapshot"
                                        variant="outlined"
                                    />
                                )}
                                size="small"
                                ListboxProps={{ style: { maxHeight: 300 } }}
                            />
                            {isLoadingSnapshots && <CircularProgress size={24} style={{ marginRight: 4 }} />}
                        <Tooltip title="Create Snapshot" classes={{ tooltip: classes.tooltip }}><span><IconButton onClick={() => createSnapshot()} disabled={isLoading || selectedLocations.length === 0} size="small" style={{ padding: 4 }}><AddIcon htmlColor={selectedLocations.length === 0 ? 'rgba(0,0,0,0.26)' : '#1976d2'} fontSize="small" /></IconButton></span></Tooltip>
                            {selectedSnapshot && <Tooltip title="Prefill from Other" classes={{ tooltip: classes.tooltip }}><span><IconButton onClick={() => setPrefillDialogOpen(true)} disabled={selectedSnapshot.status === 'finalized'} size="small" style={{ padding: 4 }}><InputIcon htmlColor={selectedSnapshot.status === 'finalized' ? 'rgba(0,0,0,0.26)' : '#1976d2'} fontSize="small" /></IconButton></span></Tooltip>}
                            {selectedSnapshot && selectedSnapshot.status !== 'finalized' && <Tooltip title="Validate Snapshot" classes={{ tooltip: classes.tooltip }}><span><IconButton onClick={() => validateSnapshot()} disabled={isLoading} size="small" style={{ padding: 4 }}><CheckCircleIcon htmlColor={isLoading ? 'rgba(0,0,0,0.26)' : '#1976d2'} fontSize="small" /></IconButton></span></Tooltip>}
                            {selectedSnapshot && selectedSnapshot.status !== 'finalized' && <Tooltip title="Create Stock Order" classes={{ tooltip: classes.tooltip }}><span><IconButton onClick={() => { setStockOrderData({ description: `Created from Konnect using ${selectedSnapshot?.snapshotNumber || ''} (ID: ${selectedSnapshot?.id || ''}) - ${moment().format('YYYY-MM-DD')}` }); setFinalizeDialogOpen(true); }} disabled={isLoading || !isValidated} size="small" style={{ padding: 4 }}><ShoppingCartIcon htmlColor={(isLoading || !isValidated) ? 'rgba(0,0,0,0.26)' : '#f50057'} fontSize="small" /></IconButton></span></Tooltip>}
                            {selectedSnapshot && selectedSnapshot.status !== 'finalized' && <FormControlLabel control={<Switch checked={bulkSaveMode} onChange={(e) => handleBulkSaveModeChange(e.target.checked)} color="primary" />} label={<Typography variant="body2">Bulk save</Typography>} style={{ marginLeft: 4 }} />}
                            {bulkSaveMode && selectedSnapshot && actualUnsavedChangesEntries.length > 0 && (
                                <Tooltip title={`Unsaved changes (${actualUnsavedChangesEntries.length}) - Click to save`} classes={{ tooltip: classes.tooltip }}>
                                    <IconButton onClick={() => setUnsavedChangesDialogOpen(true)} size="small" style={{ padding: 4, marginLeft: 2 }} aria-label={`Unsaved changes (${actualUnsavedChangesEntries.length})`}>
                                        <SaveIcon htmlColor="#1976d2" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        {/* Right: Search + Table filtering and editing actions */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 8, flexShrink: 0 }}>
                            <TextField
                                placeholder="Search..."
                                size="small"
                                variant="outlined"
                                value={searchInputValue}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInputValue ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => handleSearchChange('')} edge="end" style={{ padding: 4 }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                                style={{ minWidth: 200, maxWidth: 280 }}
                            />
                            <Tooltip title="When enabled, On Order shows remaining qty (ordered minus received). When off, shows full ordered qty on booked orders with no receipt." classes={{ tooltip: classes.tooltip }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={accountForReceived}
                                            onChange={(e) => setAccountForReceived(e.target.checked)}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="body2">Subtract received</Typography>}
                                    style={{ marginLeft: 4, marginRight: 4 }}
                                />
                            </Tooltip>
                            <Tooltip title="Refresh Data" classes={{ tooltip: classes.tooltip }}>
                                <IconButton onClick={() => handleRefresh()} size="small">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Export" classes={{ tooltip: classes.tooltip }}>
                                <IconButton onClick={handleExportClick} disabled={isExporting} size="small">
                                    <SaveAltIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={filterVisible ? 'Hide Filters' : 'Show Filters'} classes={{ tooltip: classes.tooltip }}>
                                <IconButton onClick={toggleFilterPanel} size="small">
                                    <FilterListIcon color={filterVisible ? 'primary' : 'inherit'} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Bulk Edit Order Qty" classes={{ tooltip: classes.tooltip }}>
                                <IconButton
                                    disabled={!selectedSnapshot || selectedSnapshot.status === 'finalized'}
                                    onClick={(e) => {
                                        const el = (e.currentTarget || e.target) as HTMLElement;
                                        const btn = el.closest ? el.closest('button') : null;
                                        const rect = (btn as HTMLElement)?.getBoundingClientRect?.() || (el as HTMLElement).getBoundingClientRect();
                                        setBulkEditAnchorPos({ top: rect.bottom, left: rect.right });
                                    }}
                                    size="small"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="View filter: Show all items, warnings, or no supplier" classes={{ tooltip: classes.tooltip }}>
                                <IconButton
                                    onClick={(e) => {
                                        const el = (e.currentTarget || e.target) as HTMLElement;
                                        const btn = el.closest ? el.closest('button') : null;
                                        const rect = (btn as HTMLElement)?.getBoundingClientRect?.() || (el as HTMLElement).getBoundingClientRect();
                                        setViewFilterAnchorPos({ top: rect.bottom, left: rect.right });
                                    }}
                                    size="small"
                                >
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <InfoOutlinedIcon style={{ color: viewMode !== 'all' ? orange[600] : 'inherit', marginRight: 2 }} />
                                        <ArrowDropDownIcon fontSize="small" style={{ color: viewMode !== 'all' ? orange[600] : 'inherit' }} />
                                    </span>
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                    {filterVisible && (
                        <FilterRow
                            renderAvailableFilters={<AvailableFilters filters={FILTERS} addFilter={addFilter} />}
                            renderSelectedFilters={<SelectedFilters selectedFilters={selectedFilters} removeFilter={removeFilter} updateFilter={updateFilter} businessId={selectedBusiness?.business_id || null} />}
                        />
                    )}
                </div>
                <InventoryOrderVirtualTable
                    data={reportData}
                    loading={isLoading}
                    page={tableQuery.page ?? 0}
                    pageSize={tableQuery.pageSize ?? defaultPageSize}
                    totalCount={totalRecords ?? 0}
                    onPageChange={(p) => setTableQuery((q: any) => ({ ...q, page: p }))}
                    onPageSizeChange={(ps) => setTableQuery((q: any) => ({ ...q, pageSize: ps, page: 0 }))}
                    sortConfig={tableQuery.orderBy ? { field: tableQuery.orderBy, direction: (tableQuery.orderDirection || 'asc') as 'asc' | 'desc' } : null}
                    onSort={(field) => setTableQuery((q: any) => ({
                        ...q,
                        orderBy: field,
                        orderDirection: q.orderBy === field
                            ? (q.orderDirection === 'asc' ? 'desc' : 'asc')
                            : 'asc',
                        page: 0,
                    }))}
                    editable={!!selectedSnapshot && selectedSnapshot.status !== 'finalized'}
                    unsavedChanges={Object.fromEntries(
                        Object.entries(unsavedChanges)
                            .filter(([, v]) => v.originalValue !== v.newValue)
                            .map(([k, v]) => [k, { originalValue: v.originalValue, newValue: v.newValue }])
                    )}
                    handlers={{
                        onUpdate: (newVal, row) => handleRowUpdate({ ...row, orderQtyToPlace: newVal }, row),
                        onCommitAndNavigate: handleOrderQtyCommitAndNavigate,
                    }}
                    errorIconClassName={classes.errorIcon}
                    focusRowId={focusRowId}
                    savingRowId={savingRowId}
                    emptyMessage={emptyDataSourceMessage}
                    virtualizerRef={virtualizerRef}
                />
                <ExportMenu anchorPosition={exportBtnAnchorPos} handleClose={handleExportMenuClose} />

                {/* View Filter Popover */}
                <Popover
                    open={Boolean(viewFilterAnchorPos)}
                    anchorReference="anchorPosition"
                    anchorPosition={viewFilterAnchorPos || undefined}
                    onClose={() => setViewFilterAnchorPos(null)}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <div style={{ padding: '8px 0', minWidth: 220 }}>
                        <MenuItem
                            onClick={() => {
                                setViewFilterAnchorPos(null);
                                if (viewMode !== 'all') {
                                    setViewMode('all');
                                    setSelectedWarningItemId(null);
                                    setTotalRecords(null);
                                    setTableQuery((q: any) => ({ ...q, page: 0 }));
                                    // useEffect triggers table refresh when viewMode/selectedWarningItemId change
                                }
                            }}
                            selected={viewMode === 'all'}
                        >
                            Show all items
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setViewFilterAnchorPos(null);
                                if (viewMode !== 'warnings' || selectedWarningItemId !== null) {
                                    setViewMode('warnings');
                                    setSelectedWarningItemId(null);
                                    setTotalRecords(null);
                                    setTableQuery((q: any) => ({ ...q, page: 0 }));
                                    // useEffect triggers table refresh when viewMode/selectedWarningItemId change
                                }
                            }}
                            selected={viewMode === 'warnings'}
                            disabled={warningsTotalCount === 0}
                        >
                            Show items with warnings
                            {warningsTotalCount > 0 && ` (${warningsTotalCount})`}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setViewFilterAnchorPos(null);
                                if (viewMode !== 'no_supplier') {
                                    setViewMode('no_supplier');
                                    setSelectedWarningItemId(null);
                                    setTotalRecords(null);
                                    setTableQuery((q: any) => ({ ...q, page: 0 }));
                                    // useEffect triggers table refresh when viewMode/selectedWarningItemId change
                                }
                            }}
                            selected={viewMode === 'no_supplier'}
                        >
                            Show items with no supplier
                        </MenuItem>
                    </div>
                </Popover>

                {/* Bulk Edit Popover */}
                <Popover
                    open={Boolean(bulkEditAnchorPos)}
                    anchorReference="anchorPosition"
                    anchorPosition={bulkEditAnchorPos || undefined}
                    onClose={() => setBulkEditAnchorPos(null)}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <div style={{ padding: '16px', minWidth: '280px', maxWidth: '340px' }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '12px' }}>
                            Bulk Edit Order Qty
                        </Typography>

                        {bulkEditMode !== 'RECALCULATE' && (
                            <TextField
                                label="Order Qty Value"
                                type="number"
                                value={bulkEditQty}
                                onChange={(e) => setBulkEditQty(e.target.value)}
                                inputProps={{ min: 0, step: 1 }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginBottom: '16px' }}
                            />
                        )}

                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '4px' }}>
                            Apply to:
                        </Typography>
                        <RadioGroup
                            value={bulkEditMode}
                            onChange={(e) => setBulkEditMode(e.target.value as 'ALL' | 'FILTERED' | 'VISIBLE' | 'RECALCULATE')}
                            style={{ marginBottom: '16px' }}
                        >
                            <FormControlLabel
                                value="ALL"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">All items in snapshot</Typography>}
                            />
                            <FormControlLabel
                                value="FILTERED"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">Filtered items</Typography>}
                            />
                            <FormControlLabel
                                value="VISIBLE"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">Visible items (current page)</Typography>}
                            />
                            <FormControlLabel
                                value="RECALCULATE"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">Recalculate suggested Order Qty (fix 0s)</Typography>}
                            />
                        </RadioGroup>

                        {bulkEditMode === 'VISIBLE' && reportData.length === 0 && (
                            <Typography variant="caption" style={{ color: orange[700], display: 'block', marginBottom: '8px' }}>
                                ⚠ No visible items detected. Scroll the table to load items first.
                            </Typography>
                        )}

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={() => setBulkEditAnchorPos(null)}
                                disabled={isBulkEditing}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleBulkReset}
                                disabled={isBulkEditing}
                            >
                                {isBulkEditing ? <CircularProgress size={16} /> : 'Apply'}
                            </Button>
                        </div>
                    </div>
                </Popover>
            </div>

            {/* Unsaved Changes Dialog - shows table view or reset confirmation inline */}
            <Dialog
                open={unsavedChangesDialogOpen}
                onClose={() => {
                    setUnsavedChangesDialogOpen(false);
                    setResetConfirmOpen(false);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{resetConfirmOpen ? 'Confirm Reset' : 'Unsaved Changes'}</DialogTitle>
                <DialogContent>
                    {resetConfirmOpen ? (
                        <Typography variant="body1">Are you sure you want to reset?</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell align="right">Suggested Qty</TableCell>
                                    <TableCell align="right">New Qty</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {actualUnsavedChangesEntries.map(([rowId, change]) => (
                                    <TableRow key={rowId}>
                                        <TableCell>{change.description}</TableCell>
                                        <TableCell>{change.supplier}</TableCell>
                                        <TableCell>{change.location}</TableCell>
                                        <TableCell align="right">{change.originalValue}</TableCell>
                                        <TableCell align="right">{change.newValue}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions style={{ justifyContent: 'flex-end', paddingRight: 24 }}>
                    {resetConfirmOpen ? (
                        <>
                            <Button onClick={() => setResetConfirmOpen(false)}>Cancel</Button>
                            <Button onClick={handleUnsavedChangesReset} color="secondary" variant="contained">
                                Reset
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setUnsavedChangesDialogOpen(false)}>Close</Button>
                            <Button onClick={() => setResetConfirmOpen(true)} color="secondary">
                                Reset
                            </Button>
                            <Button
                                onClick={handleUnsavedChangesSave}
                                variant="contained"
                                color="primary"
                                disabled={isSavingBulkChanges}
                            >
                                {isSavingBulkChanges ? <CircularProgress size={20} /> : 'Save'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Bulk to Single Edit Discard Confirmation */}
            <Dialog
                open={bulkToSingleDiscardConfirmOpen}
                onClose={() => setBulkToSingleDiscardConfirmOpen(false)}
            >
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        You have unsaved changes in bulk edit mode. If you switch to single edit mode now, all unsaved changes will be discarded.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                        Do you want to proceed and switch to single edit mode?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkToSingleDiscardConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleDiscardAndSwitchToSingleEdit} color="secondary" variant="contained">
                        Discard and switch
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Prefill Dialog */}
            <Dialog open={prefillDialogOpen} onClose={() => setPrefillDialogOpen(false)}>
                <DialogTitle>Prefill from Other Snapshot</DialogTitle>
                <DialogContent>
                    <Autocomplete<InventoryReportSnapshot>
                        options={snapshots.filter((s) => s.id !== selectedSnapshot?.id)}
                        getOptionLabel={(option) => `#${option.snapshotNumber} (ID: ${option.id}) - ${moment(option.createdAt).format('MM/DD/YYYY')} (${option.status})`}
                        onChange={(_, newValue) => {
                            const snapshot = newValue as InventoryReportSnapshot | null;
                            if (snapshot && typeof snapshot === 'object' && 'id' in snapshot) {
                                handlePrefillSnapshotSelect(snapshot.id);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Other Snapshot" variant="outlined" fullWidth />
                        )}
                        style={{ marginTop: 8, minWidth: 480 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPrefillDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Prefill Confirmation Dialog */}
            <Dialog
                open={prefillConfirmDialogOpen}
                onClose={() => {
                    setPrefillConfirmDialogOpen(false);
                    setSelectedPrefillSnapshotId(null);
                }}
            >
                <DialogTitle>Confirm Prefill</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to prefill this snapshot from another snapshot?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        This will override all existing quantity_to_order values in the current snapshot with values from the selected snapshot.
                    </Typography>
                    {selectedPrefillSnapshotId && (
                        <Typography variant="body2" style={{ marginTop: 12, fontWeight: 'bold' }}>
                            Selected Snapshot: #{snapshots.find(s => s.id === selectedPrefillSnapshotId)?.snapshotNumber || selectedPrefillSnapshotId}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setPrefillConfirmDialogOpen(false);
                            setSelectedPrefillSnapshotId(null);
                        }}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmPrefill}
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={20} /> : 'Confirm Prefill'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Finalize and Create Stock Order Dialog */}
            <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Finalize Snapshot and Create Stock Order</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Comment"
                        value={stockOrderData.description || ''}
                        onChange={(e) => setStockOrderData({ ...stockOrderData, description: e.target.value })}
                        multiline
                        rows={3}
                        helperText="Internal comment (not sent to supplier)"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setFinalizeDialogOpen(false);
                        setStockOrderData({});
                    }}>Cancel</Button>
                    <Button onClick={finalizeSnapshot} color="primary" variant="contained" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : 'Finalize & Create Stock Order'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    );
};

const mapStateToProps = (state: any) => {
    return {
        userData: state.userLocalData,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InventoryOrderReportScreen);

